import { useState, useEffect } from 'react';
import { ScrapbookFolder } from '@/api';
import ShareIcon from '@/assets/share.svg';

interface FolderTreeNodeProps {
  folder: ScrapbookFolder;
  level: number;
  selectedFolderId: number;
  onFolderSelect: (folder: ScrapbookFolder) => void;
  draggingEnabled?: boolean;
  divider?: boolean;
  onDragStart?: (event: React.DragEvent, folder: ScrapbookFolder) => void;
  onDrop?: (event: React.DragEvent, folder: ScrapbookFolder) => void;
}

export default function FolderTreeNode({
  folder,
  level,
  selectedFolderId,
  onFolderSelect,
  draggingEnabled = true,
  divider = false,
  onDragStart,
  onDrop,
}: FolderTreeNodeProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    if (level === 0) {
      setExpandedFolders(new Set([folder.id]));
    }
  }, [folder.id, level]);

  const toggleFolder = (folderId: number) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const hasChildren = folder.children && folder.children.length > 0;
  const isExpanded = expandedFolders.has(folder.id);

  const dividerLocation = level === 0 ? (folder.children?.findIndex((el) => el.isShared) ?? -1) : -1;

  const handleClick = () => {
    onFolderSelect(folder);
  };

  const handleDragStart = (event: React.DragEvent) => {
    if (!draggingEnabled || folder.isShared) return;
    onDragStart?.(event, folder);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!draggingEnabled) return;
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!draggingEnabled) return;
    onDrop?.(event, folder);
  };

  return (
    <div className={`ml-${level * 4} treenode`}>
      {divider && (
        <div className="mt-1 font-bold">
          <hr className="mb-1" />
          <span className="text-4 text-black">Folders shared with me</span>
        </div>
      )}

      {level < 2 && folder.hasSharing && (
        <div className="absolute inline-block h-4 w-4">
          <ShareIcon className="mt-[12px] h-4 w-4 text-gray-500" />
        </div>
      )}

      <div
        className={`fldr flex h-[42px] w-full cursor-pointer items-center rounded-[20px] px-2 font-semibold transition-colors duration-200 ${
          folder.id === selectedFolderId ? 'bg-[#ccc] font-semibold' : 'hover:bg-gray-100'
        } ${folder.isShared ? 'text-sky' : 'text-black'} ${
          folder.isShared ? 'italic' : ''
        } ${folder.hasSharing ? 'italic' : ''}`}
        draggable={draggingEnabled && level > 0 && !folder.isShared}
        onClick={handleClick}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(folder.id);
            }}
            className="mr-2 flex h-4 w-4 cursor-pointer items-center justify-center rounded text-xs"
            style={{ color: 'rgba(0, 0, 0, .54)' }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
        {!hasChildren && <div className="mr-2 w-4" />}
        <span className="text-black">{folder.name}</span>
      </div>

      {hasChildren && isExpanded && (
        <div className="ml-4">
          {folder.children?.map((child, index) => (
            <FolderTreeNode
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              onFolderSelect={onFolderSelect}
              draggingEnabled={draggingEnabled}
              divider={index === dividerLocation}
              onDragStart={onDragStart}
              onDrop={onDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
}
